'use client';

import { CheckSquare, UserCircle } from 'lucide-react';
import { matchesLocalQuery } from '../../lib/search-result';
import { isTerminalStatus } from '../../lib/task-list';
import { trpc } from '../../lib/trpc';
import { CommandGroup, CommandItem } from '../../ui/command';
import { TaskStatusBadge } from '../tasks/task-meta';

export interface ContactTasksViewProps {
  contactId: number;
  /** Live query text — this view filters its own (already small) list locally. */
  search: string;
  onSelectTask: (taskId: number) => void;
  onOpenContact: () => void;
}

/**
 * The contact drill-in: everything attached to one person, reached with Tab from a contact
 * result.
 *
 * This is the payoff for the whole palette — the next action after finding someone is almost
 * never "look at their record", it's "deal with what's open on them". Open work is listed
 * first and completed work last, with the contact's own page always available below.
 */
export function ContactTasksView({
  contactId,
  search,
  onSelectTask,
  onOpenContact,
}: ContactTasksViewProps) {
  const tasksQuery = trpc.tasks.getByContact.useQuery({ contactId });
  const statusesQuery = trpc.taskStatuses.list.useQuery();
  const statuses = statusesQuery.data?.items ?? [];

  const tasks = (tasksQuery.data ?? [])
    .filter((task) => matchesLocalQuery([task.label, task.content], search))
    .sort((a, b) => {
      const aDone = isTerminalStatus(statuses, a.status);
      const bDone = isTerminalStatus(statuses, b.status);
      if (aDone !== bDone) return aDone ? 1 : -1;
      return (b.createdAt ?? 0) - (a.createdAt ?? 0);
    });

  const openCount = tasks.filter((task) => !isTerminalStatus(statuses, task.status)).length;

  return (
    <>
      {tasksQuery.isLoading ? (
        <CommandGroup heading="Tasks">
          <div className="text-muted-foreground px-2 py-3 text-sm">Loading tasks…</div>
        </CommandGroup>
      ) : (
        tasks.length > 0 && (
          <CommandGroup heading={openCount > 0 ? `Tasks · ${openCount} open` : 'Tasks'}>
            {tasks.map((task) => {
              const done = isTerminalStatus(statuses, task.status);

              return (
                <CommandItem
                  key={task.id}
                  value={`task:${task.id} ${task.label}`}
                  onSelect={() => onSelectTask(task.id)}
                  className="gap-3"
                >
                  <span className="bg-muted text-muted-foreground flex size-6 shrink-0 items-center justify-center rounded">
                    <CheckSquare className="size-3.5" />
                  </span>
                  <span
                    className={done ? 'truncate text-muted-foreground line-through' : 'truncate'}
                  >
                    {task.label}
                  </span>
                  <TaskStatusBadge
                    statuses={statuses}
                    statusKey={task.status}
                    className="ml-auto shrink-0"
                  />
                </CommandItem>
              );
            })}
          </CommandGroup>
        )
      )}

      <CommandGroup heading="Contact">
        <CommandItem value="open-contact-page" onSelect={onOpenContact} className="gap-3">
          <span className="bg-muted text-muted-foreground flex size-6 shrink-0 items-center justify-center rounded">
            <UserCircle className="size-3.5" />
          </span>
          <span>Open contact page</span>
        </CommandItem>
      </CommandGroup>
    </>
  );
}
